import { notify } from '@billy/design-system'
import {
  createContext,
  PropsWithChildren,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import {
  getLogoAndBrandingSettingsDefaultValues,
  getLogoAndBrandingSettingsFormSchema,
  type LogoAndBrandingSettingsFormSchema,
  type LogoAndBrandingSettingsFormValues,
  usePredefinedLogoAndBrandingSettingsFormValues,
  useUpdateLogoAndBrandingTemplateSettings,
} from '@features/invoicingSettings/modules/logoAndBranding'

import { queryClient } from '../../../../../../../../../../../config/queryClient'
import { NotificationKeys } from '../../../../../../../../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../../../../../../../../enums/queryKeys'
import { useForm, UseFormReturn } from '../../../../../../../../../../../hooks/useForm'
import { useSegment } from '../../../../../../../../../../../hooks/useSegment/useSegment'
import { useUpdateLogoAndBrandingSettings } from '../hooks/useUpdateLogoAndBrandingSettings'

interface ContextState {
  handleSaveButtonClick: () => Promise<void>
  hasFailedToLoad: boolean
  isInvoicePreviewLoading: boolean
  isLoading: boolean
  isProcessing: boolean
  logoAndBrandingSettingsForm: UseFormReturn<LogoAndBrandingSettingsFormSchema>
  refetch: () => Promise<unknown>
}

const InvoicePreviewAppearanceFormContext = createContext<ContextState | undefined>(undefined)

interface InvoicePreviewAppearanceFormContextProviderProps extends PropsWithChildren {
  isInvoicePreviewLoading?: boolean
  onDirtyChange?: (isDirty: boolean) => void
  onSubmit?: () => void
}

export const InvoicePreviewAppearanceFormContextProvider = ({
  children,
  isInvoicePreviewLoading = false,
  onDirtyChange,
  onSubmit,
}: InvoicePreviewAppearanceFormContextProviderProps): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const [isFormInitialized, setIsFormInitialized] = useState(false)

  const {
    hasFailedToLoad,
    isLoading: isDataLoading,
    predefinedLogoAndBrandingSettingsFormValues,
    refetch,
  } = usePredefinedLogoAndBrandingSettingsFormValues()
  const { update: updateInvoiceSettings, isProcessing: isProcessingInvoiceSettings } =
    useUpdateLogoAndBrandingSettings()
  const { update: updateInvoiceTemplate, isProcessing: isProcessingTemplate } =
    useUpdateLogoAndBrandingTemplateSettings()

  const logoAndBrandingSettingsForm = useForm<LogoAndBrandingSettingsFormSchema>({
    defaultValues: getLogoAndBrandingSettingsDefaultValues(),
    validationSchema: getLogoAndBrandingSettingsFormSchema(t),
  })
  const {
    Form,
    formState: { isDirty },
    handleSubmit,
    reset: resetForm,
  } = logoAndBrandingSettingsForm

  const isProcessing = isProcessingInvoiceSettings || isProcessingTemplate

  // isLoading stays true until the form has been populated with real server data AND the invoice
  // payload is ready. This prevents firing a preview request with placeholder default values before
  // predefined form values have been loaded, which would race against the correct request.
  // A failed fetch must not stay on the skeleton, or the retry UI never appears.
  const isLoading = isDataLoading || (!hasFailedToLoad && !isFormInitialized)

  const handleLogoAndBrandingSettingsFormSubmit = useCallback(
    async (props: LogoAndBrandingSettingsFormValues) => {
      try {
        await Promise.all([updateInvoiceSettings(props), updateInvoiceTemplate(props)])

        // Clears dirty state right away, rather than waiting on the slower invalidate-and-refetch below
        resetForm(props)

        // The settings writer seeds its cache from the PUT response. The template mutation already
        // refreshed its query. The logo query is separate.
        await queryClient.invalidateQueries(QueryKeys.ZervantInvoiceLogo)
        track('XXX - AG - Invoice Preview - Appearance Changes Saved')

        notify({
          id: NotificationKeys.ExternalInvoiceSettingsUpdate,
          message: t('settings.organization.external_invoice.save.success'),
          variant: 'success',
        })

        onSubmit?.()
      } catch {
        notify({
          id: NotificationKeys.ExternalInvoiceSettingsUpdate,
          message: t('error_saving'),
          variant: 'error',
        })
      }
    },
    [onSubmit, resetForm, t, track, updateInvoiceTemplate, updateInvoiceSettings],
  )

  const handleSaveButtonClick = useCallback((): Promise<void> => {
    return handleSubmit(handleLogoAndBrandingSettingsFormSubmit)()
  }, [handleSubmit, handleLogoAndBrandingSettingsFormSubmit])

  // Skip while dirty, so a half-finished save cannot reset the form. isDirty is not a dependency.
  // Skip a failed fetch so empty defaults are not written into the form.
  useEffect(() => {
    if (isDataLoading || hasFailedToLoad || isDirty) {
      return
    }

    const updatedFormValues = getLogoAndBrandingSettingsDefaultValues(predefinedLogoAndBrandingSettingsFormValues)

    resetForm(updatedFormValues)
    setIsFormInitialized(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isDirty is a guard, not a dependency: a second run after save would reset from predefined values with a stale logoUrl
  }, [hasFailedToLoad, isDataLoading, predefinedLogoAndBrandingSettingsFormValues, resetForm])

  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  const contextValue = useMemo(
    () => ({
      handleSaveButtonClick,
      hasFailedToLoad,
      isInvoicePreviewLoading,
      isLoading,
      isProcessing,
      logoAndBrandingSettingsForm,
      refetch,
    }),
    [
      handleSaveButtonClick,
      hasFailedToLoad,
      isInvoicePreviewLoading,
      isLoading,
      isProcessing,
      logoAndBrandingSettingsForm,
      refetch,
    ],
  )

  return (
    <InvoicePreviewAppearanceFormContext.Provider value={contextValue}>
      <Form>{children}</Form>
    </InvoicePreviewAppearanceFormContext.Provider>
  )
}

export const useInvoicePreviewAppearanceForm = () => {
  const context = useContext(InvoicePreviewAppearanceFormContext)

  if (!context) {
    throw new Error('InvoicePreviewAppearanceFormContextProvider is missing in the module!')
  }

  return context
}
